import {
  LitElement,
  html
} from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

import LdsIcon from '../LdsIcon/index.js';
import ButtonClearStyle from '../Common/ButtonClearStyle.js';
import TooltipStyle from './style.js';

export class LdsTooltip extends LitElement {
  static get styles() {
    return [ButtonClearStyle, TooltipStyle];
  }

  static properties = {
    closeDelay: { type: Number },
    closeIconLabel: { type: String },
    containerId: { type: String },
    defaultOpen: { type: Boolean },
    edgePadding: { type: Number },
    hideIcon: { type: Boolean },
    iconFill: { attribute: false },
    iconLabel: { type: String },
    open: { attribute: false },
    openDelay: { type: Number },
    persistent: { attribute: false },
    position: { attribute: false },
    timerOpen: { attribute: false },
    timerClose: { attribute: false },
    timerTooltipState: { attribute: false },
    tooltipArrowLeft: { attribute: false },
    tooltipStyles: { attribute: false },
    width: { type: String },
  };

  constructor() {
    super();
    this.closeDelay = 500;
    this.closeIconLabel = 'Close tooltip';
    this.containerId = null;
    this.edgePadding = 16;
    this.hideIcon = false;
    this.iconFill = 'var(--lds-color-tooltip-icon)';
    this.iconLabel = 'More information';
    this.open = false;
    this.openDelay = 400;
    this.persistent = false;
    this.position = 'top';
    this.timerOpen = null;
    this.timerClose = null;
    this.tooltipArrowLeft = '200px';
    this.tooltipStyles = {
      marginLeft: '-200px',
      width: '400px',
      visibility: 'visible',
    };
    this.width = 400;

    this.addEventListener('mouseleave', this.handleMouseLeave);
  }

  /**
   * Lit lifecycle event https://lit.dev/docs/components/lifecycle/
   * Adds event listeners for the ESC key and document click
   */
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keyup', (e) => this.handleKeyup(e));
    document.addEventListener('click', (e) => this.handleDocumentClick(e));
  }

  /**
   * Lit lifecycle event https://lit.dev/docs/components/lifecycle/
   * Removes event listeners
   */
  disconnectedCallback() {
    window.removeEventListener('keyup', (e) => this.handleKeyup(e));
    document.removeEventListener('click', (e) => this.handleDocumentClick(e));
    super.disconnectedCallback();
  }

  /**
   * Lit lifecycle event https://lit.dev/docs/components/lifecycle/
   * Applies tooltip open / close event if the attribute changes
   */
  updated(changedProperties) {
    if (changedProperties.has('defaultOpen')) {
      if (this.defaultOpen) {
        this.openTooltip();
      } else {
        this.closeTooltip();
      }
    }
  }

  /**
   * Closes the tooltip and applies animation
   */
  closeTooltip() {
    // Add transition classes. Opacity will switch to 0
    // Wait 200ms for transition to finish before removing el
    const popup = this.renderRoot.querySelector('#lds-tooltip-description');
    popup.classList.add('lds-tooltip-enter');
    popup.classList.remove('lds-tooltip-enter-to');

    setTimeout(() => {
      this.open = false;
      this.persistent = false;
    }, 200);
  }

  /**
   * Function to determine content boundary of element containing the tooltip
   * Used to keep the tooltip inside of a specific element to avoid overlap issues
   * @returns {object|false}
   */
  getContainerBoundaryRect() {
    if (this.containerId) {
      return window.document.getElementById(this.containerId).getBoundingClientRect();
    }
    return false;
  }

  /**
   * Determines x position for the arrow on tooltip description popup
   * @returns {integer}
   */
  getTooltipArrowPosition() {
    const textRect = this.renderRoot.querySelector('#tooltipTextBtn').getBoundingClientRect();
    const descriptionRect = this.renderRoot.querySelector('#lds-tooltip-description').getBoundingClientRect();

    return parseInt(textRect.left - descriptionRect.left + (textRect.width / 2) + 2, 10);
  }

  /**
   * Returns style class object to be used in classMap in render
   * @returns {object}
   */
  getTooltipClasses() {
    return {
      persistent: this.persistent,
      top: this.position === 'top',
      bottom: this.position === 'bottom',
    };
  }

  /**
   * Returns desired margin left for tooltip
   * @returns {number}
   */
  getTooltipMarginLeft() {
    const offscreenX = this.getTooltipOutsideBoundariesX();
    return -(this.getTooltipWidth() / 2) - offscreenX;
  }

  /**
   * Determines the x boundary for the tooltip. Looks at container boundary (if set)
   * as well as the edge padding and the location of the tooltip text
   * @returns {number}
   */
  getTooltipOutsideBoundariesX() {
    const containerBoundaryRect = this.getContainerBoundaryRect();

    let offsetLeft = 0;
    let offsetRight = 0;

    // Container boundary used to contain the popup in a specific area such as an accordion
    if (containerBoundaryRect) {
      offsetLeft = containerBoundaryRect.left;
      offsetRight = window.innerWidth - (containerBoundaryRect.left + containerBoundaryRect.width);
    }

    /* eslint-enable max-len */
    const rect = this.renderRoot.querySelector('#lds-tooltip-container').getBoundingClientRect();
    const projectedXCoordinate = rect.left + (rect.width / 2) - (this.getTooltipWidth() / 2);
    const projectedRightEdgeCoordinate = projectedXCoordinate + this.getTooltipWidth();

    // Off left side of screen
    if ((projectedXCoordinate - offsetLeft) < this.edgePadding) {
      return Math.floor((projectedXCoordinate - offsetLeft) - this.edgePadding + 1);
    }

    // Off right side of screen
    if ((projectedRightEdgeCoordinate + offsetRight) > window.innerWidth - this.edgePadding) {
      return Math.ceil(
        (projectedRightEdgeCoordinate + offsetRight) - window.innerWidth + this.edgePadding + 1
      );
    }

    return 0;
  }

  /**
   * Gets the desired width of the tooltip description popup
   * @returns {number}
   */
  getTooltipWidth() {
    return window.innerWidth - (this.edgePadding * 2) <= this.width
      ? window.innerWidth - (this.edgePadding * 2) - 2 : this.width;
  }

  /**
   * Event handler when document is clicked
   * Closes tooltip when clicking outside of the tooltip
   * @param {*} e event
   */
  handleDocumentClick(e) {
    // Did we click on the component - or outside of it?
    if (!e.composedPath().includes(this)) {
      this.closeTooltip();
    }
  }

  /**
   * Event handler for blur event
   * Closes the tooltip if not persistent (clicked)
   */
  handleBlur() {
    if (this.persistent) return;
    this.closeTooltip();
  }

  /**
   * Event handler for when the tooltip itself is clicked
   * Closes the tooltip if already open
   * Makes the tooltip persistent if not yet open
   */
  handleClick() {
    if (this.open && (this.persistent)) this.closeTooltip();
    else {
      this.persistent = true;
      this.openTooltip();
    }
  }

  /**
   * Event handler for when the tooltip description popup is clicked
   * Sets the tooltip to persistent
   */
  handleClickDescription() {
    // tooltip popup with description has been clicked.
    this.persistent = true;
  }

  /**
   * Event handler when the tooltip text is focused
   * Tooltip should open on focus
   */
  handleFocus() {
    this.openTooltip();
  }

  /**
   * Event handler facilitates pressing of the escape key
   * Tooltip should close if escape is pressed
   * @param {*} e event
   */
  handleKeyup(e) {
    if (!this.open) return;
    if (e.key === 'Escape') this.closeTooltip();
  }

  /**
   * Event handler facilitates opening of the tooltip if the mouse hovers over it
   * Opens after specified open delay in milliseconds
   * Changes the icon style to reflect hovering
   */
  handleMouseenter() {
    this.iconFill = 'var(--lds-color-tooltip-icon-hover)';

    if (this.open) return;

    clearTimeout(this.timerTooltipState);
    this.timerTooltipState = setTimeout(() => {
      this.openTooltip();
    }, this.openDelay);
  }

  /**
   * Event handler facilitates closing of the tooltip if it was hovered and then the mouse leaves
   * Closes after specified close delay
   */
  handleMouseLeave() {
    this.iconFill = 'var(--lds-color-tooltip-icon)';

    if (!this.open || this.persistent) return;

    clearTimeout(this.timerTooltipState);
    this.timerTooltipState = setTimeout(() => {
      this.closeTooltip();
    }, this.closeDelay);
  }

  /**
   * Generates the html string for the hide icon
   * @returns {string}
   */
  htmlIconInfo() {
    if (this.hideIcon) return null;
    return html`<lds-icon class="tooltip-icon" decorative="true"  name="InfoFill" inline fill="${this.iconFill}" label="${this.iconLabel}"></lds-icon>`;
  }

  /**
   * Generates the html string for the close icon
   * @returns {string}
   */
  htmlIconClose() {
    if (!this.persistent) return '';
    return html`
      <button id="btnClose" class="lds-button-base tooltip-close" @click="${() => this.closeTooltip()}">
        <lds-icon role="button" name="XCircleFill" fill="${this.iconFill}" inline label="${this.closeIconLabel}"></lds-icon>
      </button>
    `;
  }

  /**
   * Determines if the tooltip popup would open above its boundary (top of page or specified boundary)
   * @returns {boolean}
   */
  isTooltipOffscreenY() {
    const boundaryTop = this.getContainerBoundaryRect() ? this.getContainerBoundaryRect().top : false;

    const descriptionHeight = this.renderRoot.querySelector('#lds-tooltip-description').offsetHeight;
    const textRect = this.renderRoot.querySelector('#tooltipTextBtn').getBoundingClientRect().top;

    if (boundaryTop) {
      return textRect - boundaryTop - this.edgePadding < descriptionHeight;
    }

    return textRect - descriptionHeight - this.edgePadding < this.edgePadding;
  }

  /**
   * Opens the tooltip and applies corresponding animations
   */
  async openTooltip() {
    this.open = true;
    const popup = this.renderRoot.querySelector('#lds-tooltip-description');

    this.tooltipStyles.marginLeft = `${this.getTooltipMarginLeft()}px`;
    this.tooltipStyles.width = `${this.getTooltipWidth()}px`;

    await super.getUpdateComplete();

    this.position = this.isTooltipOffscreenY() ? 'bottom' : 'top';
    this.tooltipArrowLeft = `${this.getTooltipArrowPosition()}px`;
    popup.classList.add('lds-tooltip-enter-to');
  }

  render() {
    return html`<span
        id="lds-tooltip"
        class="lds-tooltip ${classMap({ persistent: this.persistent, open: this.open })}"
      >
        <span
          id="lds-tooltip-container"
          class="tooltip-container"
          @click="${this.handleClick}"
          @mouseenter="${this.handleMouseenter}"
        >
          <button
            id="tooltipTextBtn"
            class="lds-button-base tooltip-text"
            aria-describedby="lds-tooltip-description"
            @focus="${this.handleFocus}"
            @blur=${this.handleBlur}
          >
            <slot name="text"></slot>
          </button>${this.htmlIconInfo()}
        </span>

        <span
          id="lds-tooltip-description"
          class="tooltip-description lds-tooltip-enter ${classMap(this.getTooltipClasses())}"
          style=${styleMap({ width: this.width, ...this.tooltipStyles })}
          aria-hidden=${!this.open}
          role="tooltip"
          @click="${this.handleClickDescription}"
        >
          <span
            style="${styleMap({ left: this.tooltipArrowLeft })}"
            class="lds-tooltip-arrow"
          ></span>
          <slot name="description"></slot>
          ${this.htmlIconClose()}
        </span>
      </span>`;
  }
}

export function defineComponent() {
  if (!window.customElements.get('lds-tooltip')) window.customElements.define('lds-tooltip', LdsTooltip);
  LdsIcon.defineComponent();
}

export default { LdsTooltip, defineComponent };
